import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import StatusBadge from "@/components/StatusBadge";
import ParticleField from "@/components/ParticleField";
import { useProjects } from "@/lib/useProjects";

const STAGES = ["CONCEPT", "RESEARCH", "PROTOTYPE", "DEVELOPMENT", "PRIVATE BETA", "COMING SOON"];

function LabCard({ p }) {
  const accent = p.accent || "#d4af37";
  return (
    <div data-testid={`lab-card-${p.slug}`} className="card-hover group relative overflow-hidden rounded-2xl border border-white/10 bg-ink-800 p-8">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20 blur-3xl transition-opacity group-hover:opacity-40" style={{ background: accent }} />
      <div className="relative z-10 flex items-center justify-between">
        <span className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{p.category || p.industry}</span>
        <StatusBadge status={p.development_stage || p.status} />
      </div>
      <h3 className="relative z-10 mt-6 font-display text-3xl font-bold tracking-tighter text-white">{p.name}</h3>
      <p className="relative z-10 mt-2 font-display text-lg font-light" style={{ color: accent }}>{p.tagline}</p>
      <p className="relative z-10 mt-4 font-body text-sm leading-relaxed text-white/55">{p.long_description || p.description}</p>
      <Link to={`/technology/${p.slug}`} className="relative z-10 mt-6 inline-flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-gold-light hover:text-white">
        Explore Concept <ArrowRight size={13} />
      </Link>
    </div>
  );
}

export default function Labs() {
  const { projects } = useProjects();
  const labs = projects.filter((p) => p.imjove_labs);
  const pipeline = projects.filter((p) => !p.imjove_labs && ["IN DEVELOPMENT", "CONCEPT", "PROTOTYPE", "PRIVATE BETA", "COMING SOON"].includes(p.status));

  return (
    <div>
      <SEO title="ImJove Labs — Tomorrow Is Already Being Built" description="A look inside the technologies, concepts, and experiments explored by Jove Divinity and ImJove Tech." />
      <section className="relative overflow-hidden pb-16 pt-40">
        <ParticleField className="absolute inset-0 z-0" density={45} />
        <div className="radial-fade absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">ImJove Labs</p>
            <h1 className="mt-4 max-w-4xl font-display text-5xl font-bold tracking-tighter text-white sm:text-7xl">
              Tomorrow is <span className="text-gold-gradient">already being built.</span>
            </h1>
            <p className="mt-6 max-w-2xl font-body text-lg text-white/55">
              ImJove Labs gives the world a look inside the technologies, concepts, experiments, and ideas currently being explored by Jove Divinity and ImJove Tech.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {STAGES.map((s) => (
                <span key={s} className="font-mono2 text-[10px] uppercase tracking-widest text-white/35">{s}<span className="mx-2 text-white/15">/</span></span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-6 md:grid-cols-2">
            {labs.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}><LabCard p={p} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {pipeline.length > 0 && (
        <section className="border-t border-white/10 bg-ink-800 py-24">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl">In the pipeline</h2>
              <p className="mt-3 font-body text-white/50">Ecosystem platforms currently in development.</p>
            </Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {pipeline.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}><LabCard p={p} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
